import React, { useState, useEffect, useCallback } from 'react';
import { useResumeRefresh } from '../hooks/useResumeRefresh';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Calendar, Clock, CheckCircle2, AlertTriangle, Upload, Send, 
  MessageSquare, ChevronDown, ChevronRight, Plus, Trash2, Edit2,
  TrendingUp, DollarSign, CloudRain, Bell, RefreshCw, ArrowLeft,
  FileSpreadsheet, Sparkles, Users, X, LayoutGrid, List, Save, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';

const ProjectSchedule = ({ embedded = false, embeddedProjectId = null } = {}) => {
  const params = useParams();
  const projectId = embeddedProjectId || params.projectId;
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [project, setProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [healthData, setHealthData] = useState(null);
  const [cashFlowData, setCashFlowData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPhases, setExpandedPhases] = useState({});
  
  // View mode: 'card' or 'table'
  const [viewMode, setViewMode] = useState('table');
  
  // Dialogs
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showComparisonDialog, setShowComparisonDialog] = useState(false);
  const [showAddTaskDialog, setShowAddTaskDialog] = useState(false);
  const [showSendScheduleDialog, setShowSendScheduleDialog] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showHealthDialog, setShowHealthDialog] = useState(false);
  const [showCashFlowDialog, setShowCashFlowDialog] = useState(false);
  const [showEditTaskDialog, setShowEditTaskDialog] = useState(false);
  const [showEditPhaseDialog, setShowEditPhaseDialog] = useState(false);
  const [showSendItemsDialog, setShowSendItemsDialog] = useState(false); // NEW: Option 3 dialog
  
  // Form state
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [selectedTasksToSend, setSelectedTasksToSend] = useState({}); // NEW: For Option 3
  const [sendMessage, setSendMessage] = useState(''); // NEW: Optional message
  const [uploadFile, setUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [newTask, setNewTask] = useState({ name: '', description: '', start_date: '', end_date: '' });
  const [editTask, setEditTask] = useState(null);
  const [editPhase, setEditPhase] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [comparisonData, setComparisonData] = useState(null);
  const [selectedChanges, setSelectedChanges] = useState({});
  const [phaseMappings, setPhaseMappings] = useState({}); // NEW: For Option B - map schedule items to phases

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const loadData = useCallback(async () => {
    if (!user?.id || !projectId) return;
    
    setLoading(true);
    try {
      const [projectRes, phasesRes, tasksRes, approvalsRes, contractorsRes, healthRes, alertsRes] = await Promise.all([
        apiService.getProject(projectId),
        apiService.getPhases(projectId),
        apiService.getScheduleTasks(projectId, user.id),
        apiService.getScheduleApprovals(projectId, user.id),
        apiService.getContractorsByUser(user.id),
        apiService.getAIHealthAnalysis(projectId, user.id),
        apiService.getProjectAlerts(projectId, user.id)
      ]);
      
      setProject(projectRes.data);
      setPhases(phasesRes.data || []);
      setTasks(tasksRes.data || []);
      setApprovals(approvalsRes.data || []);
      setContractors(contractorsRes.data || []);
      setHealthData(healthRes.data);
      setAlerts(alertsRes.data || []);
      
      // Auto-expand phases with tasks
      const expanded = {};
      (tasksRes.data || []).forEach(t => { expanded[t.phase_id] = true; });
      setExpandedPhases(expanded);
      
    } catch (error) {
      console.error('Error loading schedule data:', error);
      toast.error('Failed to load schedule data');
    } finally {
      setLoading(false);
    }
  }, [user?.id, projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  useResumeRefresh(loadData);

  const handleUploadSchedule = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    
    setIsUploading(true);
    try {
      const result = await apiService.importSchedule(projectId, user.id, uploadFile);
      
      if (result.data.status === 'preview') {
        // Show comparison dialog
        setComparisonData(result.data);
        
        // Pre-select all changes
        const initialSelections = {};
        result.data.comparison.forEach((phase, pIdx) => {
          initialSelections[`phase_${pIdx}`] = phase.has_changes;
          phase.tasks.forEach((task, tIdx) => {
            initialSelections[`task_${pIdx}_${tIdx}`] = task.has_changes;
          });
        });
        setSelectedChanges(initialSelections);
        
        setShowUploadDialog(false);
        setShowComparisonDialog(true);
        toast.success('Schedule analyzed - review changes below');
      } else {
        toast.success('Schedule imported successfully');
        setShowUploadDialog(false);
        loadData();
      }
      setUploadFile(null);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to import schedule');
    } finally {
      setIsUploading(false);
    }
  };

  const handleApplyChanges = async () => {
    if (!comparisonData) return;
    
    const changes = comparisonData.comparison.map((phase, pIdx) => {
      const applyPhase = selectedChanges[`phase_${pIdx}`] !== false;
      // Get the linked phase for this group (Option B - user selected mapping)
      const linkedPhaseId = phaseMappings[`phase_${pIdx}`] || null;
      
      return {
        phase_name: phase.name,
        phase_id: phase.phase_id,
        linked_phase_id: linkedPhaseId, // NEW: Optional phase link
        apply_phase: applyPhase,
        new_start_date: phase.new?.start_date,
        new_end_date: phase.new?.end_date,
        tasks: phase.tasks.map((task, tIdx) => ({
          name: task.name,
          task_id: task.task_id,
          apply: selectedChanges[`task_${pIdx}_${tIdx}`] !== false,
          new_start_date: task.new?.start_date,
          new_end_date: task.new?.end_date,
          description: task.new?.description,
          order: task.new?.order,
          linked_phase_id: phaseMappings[`task_${pIdx}_${tIdx}`] || linkedPhaseId // Task can override or inherit
        }))
      };
    });
    
    try {
      const result = await apiService.applyScheduleChanges(projectId, user.id, changes);
      const linked = result.data.linked_tasks || 0;
      const unlinked = result.data.unlinked_tasks || 0;
      toast.success(`Added ${result.data.created_tasks || 0} schedule items (${linked} linked to phases, ${unlinked} standalone)`);
      setShowComparisonDialog(false);
      setComparisonData(null);
      setSelectedChanges({});
      setPhaseMappings({});
      loadData();
    } catch (error) {
      console.error('Apply changes error:', error);
      toast.error('Failed to apply changes');
    }
  };

  const togglePhaseSelection = (pIdx) => {
    setSelectedChanges(prev => ({
      ...prev,
      [`phase_${pIdx}`]: !prev[`phase_${pIdx}`]
    }));
  };

  const toggleTaskSelection = (pIdx, tIdx) => {
    setSelectedChanges(prev => ({
      ...prev,
      [`task_${pIdx}_${tIdx}`]: !prev[`task_${pIdx}_${tIdx}`]
    }));
  };

  const selectAllChanges = (select) => {
    if (!comparisonData) return;
    const newSelections = {};
    comparisonData.comparison.forEach((phase, pIdx) => {
      if (phase.has_changes) newSelections[`phase_${pIdx}`] = select;
      phase.tasks.forEach((task, tIdx) => {
        if (task.has_changes) newSelections[`task_${pIdx}_${tIdx}`] = select;
      });
    });
    setSelectedChanges(newSelections);
  };

  const handleAddTask = async () => {
    if (!selectedPhase || !newTask.name || !newTask.start_date || !newTask.end_date) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      await apiService.createScheduleTask(projectId, user.id, {
        phase_id: selectedPhase.id,
        name: newTask.name,
        description: newTask.description,
        start_date: new Date(newTask.start_date).toISOString(),
        end_date: new Date(newTask.end_date).toISOString()
      });
      toast.success('Task added');
      setShowAddTaskDialog(false);
      setNewTask({ name: '', description: '', start_date: '', end_date: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    
    try {
      await apiService.deleteScheduleTask(taskId, user.id);
      toast.success('Task deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleEditTask = async () => {
    if (!editTask || !editTask.name || !editTask.start_date || !editTask.end_date) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      await apiService.updateScheduleTask(editTask.id, user.id, {
        name: editTask.name,
        description: editTask.description,
        start_date: new Date(editTask.start_date).toISOString(),
        end_date: new Date(editTask.end_date).toISOString(),
        status: editTask.status
      });
      toast.success('Task updated');
      setShowEditTaskDialog(false);
      setEditTask(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleEditPhase = async () => {
    if (!editPhase || !editPhase.name) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      await apiService.updatePhase(editPhase.id, {
        name: editPhase.name,
        start_date: editPhase.start_date ? new Date(editPhase.start_date).toISOString() : null,
        end_date: editPhase.end_date ? new Date(editPhase.end_date).toISOString() : null,
        status: editPhase.status
      });
      toast.success('Phase updated');
      setShowEditPhaseDialog(false);
      setEditPhase(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update phase');
    }
  };

  const openEditTask = (task) => {
    setEditTask({
      ...task,
      start_date: task.start_date ? new Date(task.start_date).toISOString().split('T')[0] : '',
      end_date: task.end_date ? new Date(task.end_date).toISOString().split('T')[0] : ''
    });
    setShowEditTaskDialog(true);
  };

  const openEditPhase = (phase) => {
    setEditPhase({
      ...phase,
      start_date: phase.start_date ? new Date(phase.start_date).toISOString().split('T')[0] : '',
      end_date: phase.end_date ? new Date(phase.end_date).toISOString().split('T')[0] : ''
    });
    setShowEditPhaseDialog(true);
  };

  const handleSendSchedule = async () => {
    if (!selectedPhase || !selectedContractor) {
      toast.error('Please select phase and contractor');
      return;
    }
    
    try {
      await apiService.sendScheduleToContractor(projectId, selectedPhase.id, selectedContractor, user.id);
      toast.success('Schedule sent to contractor');
      setShowSendScheduleDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to send schedule');
    }
  };

  // NEW: Option 3 - Send selected schedule items
  const handleSendScheduleItems = async () => {
    const selectedIds = Object.entries(selectedTasksToSend)
      .filter(([_, selected]) => selected)
      .map(([id]) => id);
    
    if (selectedIds.length === 0) {
      toast.error('Please select at least one schedule item');
      return;
    }
    if (!selectedContractor) {
      toast.error('Please select a contractor');
      return;
    }
    
    try {
      const result = await apiService.sendScheduleItemsToContractor(
        projectId, 
        selectedContractor, 
        selectedIds, 
        user.id,
        sendMessage || null
      );
      toast.success(`Sent ${result.data.items_sent} items to contractor`);
      setShowSendItemsDialog(false);
      setSelectedTasksToSend({});
      setSelectedContractor(null);
      setSendMessage('');
      loadData();
    } catch (error) {
      console.error('Send schedule items error:', error);
      toast.error('Failed to send schedule items');
    }
  };

  const toggleTaskToSend = (taskId) => {
    setSelectedTasksToSend(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const selectAllTasksToSend = () => {
    const allSelected = {};
    tasks.forEach(t => { allSelected[t.id] = true; });
    setSelectedTasksToSend(allSelected);
  };

  const deselectAllTasksToSend = () => {
    setSelectedTasksToSend({});
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedPhase || !selectedContractor) {
      toast.error('Please enter a message');
      return;
    }
    
    const contractor = contractors.find(c => c.id === selectedContractor);
    
    try {
      await apiService.sendScheduleMessage(user.id, {
        project_id: projectId,
        phase_id: selectedPhase.id,
        recipient_type: 'contractor',
        recipient_id: selectedContractor,
        subject: `RE: ${selectedPhase.name}`,
        message: messageText
      });
      toast.success(`Message sent to ${contractor?.company || contractor?.name}`);
      setShowMessageDialog(false);
      setMessageText('');
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const loadCashFlow = async () => {
    try {
      const res = await apiService.getCashFlowForecast(projectId, user.id);
      setCashFlowData(res.data);
      setShowCashFlowDialog(true);
    } catch (error) {
      toast.error('Failed to load cash flow data');
    }
  };

  const handleExportPdf = async () => {
    try {
      toast.info('Generating PDF...');
      const response = await apiService.exportSchedulePdf(projectId, user.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `schedule_${project?.name?.replace(/\s+/g, '_') || 'project'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const handleExportExcel = async () => {
    try {
      toast.info('Generating Excel...');
      const response = await apiService.exportScheduleExcel(projectId, user.id);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `schedule_${project?.name?.replace(/\s+/g, '_') || 'project'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-700',
      ahead: 'bg-emerald-100 text-emerald-700',
      in_progress: 'bg-blue-100 text-blue-700',
      behind: 'bg-red-100 text-red-700',
      delayed: 'bg-red-100 text-red-700',
      not_started: 'bg-slate-100 text-slate-600',
      pending: 'bg-slate-100 text-slate-600'
    };
    const labels = {
      completed: 'Completed',
      ahead: 'Ahead',
      in_progress: 'In Progress',
      behind: 'Behind',
      delayed: 'Delayed',
      not_started: 'Not Started',
      pending: 'Not Started'
    };
    return <Badge className={styles[status] || styles.pending}>{labels[status] || status}</Badge>;
  };

  const getApprovalBadge = (phaseId, contractorId) => {
    const approval = approvals.find(a => a.phase_id === phaseId && a.contractor_id === contractorId);
    if (!approval) return null;
    
    const styles = {
      approved: 'bg-green-100 text-green-700',
      pending: 'bg-amber-100 text-amber-700',
      change_requested: 'bg-red-100 text-red-700'
    };
    const labels = {
      approved: 'Approved',
      pending: 'Pending',
      change_requested: 'Change Requested'
    };
    return <Badge className={styles[approval.status]}>{labels[approval.status]}</Badge>;
  };

  const getPhaseTasksCount = (phaseId) => {
    return tasks.filter(t => t.phase_id === phaseId).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-slate-600">Loading schedule...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'min-h-screen bg-slate-50'}>
      {/* Header */}
      {embedded ? (
        <div className="flex flex-wrap items-center justify-end gap-2 mb-4" data-testid="schedule-embedded-toolbar">
          <Button onClick={() => { setShowHealthDialog(true); }} size="sm"
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white" data-testid="ai-health-btn">
            <Sparkles className="w-4 h-4 mr-2" />AI Health
          </Button>
          <Button onClick={loadCashFlow} size="sm" variant="outline">
            <DollarSign className="w-4 h-4 mr-2" />Cash Flow
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" data-testid="schedule-export-btn">
                <Download className="w-4 h-4 mr-2" />Export<ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportPdf} className="cursor-pointer">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-red-500" />Download as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportExcel} className="cursor-pointer">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />Download as Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => setShowUploadDialog(true)} size="sm" variant="outline" data-testid="schedule-import-btn">
            <Upload className="w-4 h-4 mr-2" />Import
          </Button>
          <Button onClick={() => setShowSendItemsDialog(true)} size="sm" className="bg-green-600 hover:bg-green-700 text-white">
            <Send className="w-4 h-4 mr-2" />Send to Contractor
          </Button>
        </div>
      ) : (
      <div className="bg-slate-800 text-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-white hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="h-6 w-px bg-slate-600" />
            <div>
              <h1 className="text-xl font-bold">Project Schedule</h1>
              <p className="text-sm text-slate-400">{project?.name}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => { setShowHealthDialog(true); }}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              data-testid="ai-health-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              AI Health Analysis
            </Button>
            <Button onClick={loadCashFlow} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
              <DollarSign className="w-4 h-4 mr-2" />
              Cash Flow
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                  <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportPdf} className="cursor-pointer">
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-red-500" />
                  Download as PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportExcel} className="cursor-pointer">
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                  Download as Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={() => setShowUploadDialog(true)} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
              <Upload className="w-4 h-4 mr-2" />
              Upload Schedule
            </Button>
            <Button onClick={() => setShowSendItemsDialog(true)} className="bg-green-600 hover:bg-green-700 text-white">
              <Send className="w-4 h-4 mr-2" />
              Send to Contractor
            </Button>
          </div>
        </div>
      </div>
      )}

      <div className={embedded ? '' : 'max-w-7xl mx-auto p-6'}>
        {/* Summary Cards */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Total Phases</p>
              <p className="text-2xl font-bold text-slate-900">{phases.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Completed</p>
              <p className="text-2xl font-bold text-green-600">{healthData?.schedule?.completed || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">In Progress</p>
              <p className="text-2xl font-bold text-blue-600">{healthData?.schedule?.in_progress || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Behind</p>
              <p className="text-2xl font-bold text-red-600">{healthData?.schedule?.behind || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Est. Completion</p>
              <p className="text-2xl font-bold text-slate-900">
                {healthData?.schedule?.predicted_completion 
                  ? formatDate(healthData.schedule.predicted_completion) 
                  : 'TBD'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <div className="mb-6 space-y-2">
            {alerts.slice(0, 3).map((alert, idx) => (
              <div 
                key={alert.id || idx} 
                className={`flex items-center gap-3 p-3 rounded-lg ${
                  alert.severity === 'critical' ? 'bg-red-50 text-red-800' :
                  alert.severity === 'warning' ? 'bg-amber-50 text-amber-800' :
                  'bg-blue-50 text-blue-800'
                }`}
              >
                {alert.severity === 'critical' ? <AlertTriangle className="w-5 h-5" /> :
                 alert.severity === 'warning' ? <Bell className="w-5 h-5" /> :
                 <Bell className="w-5 h-5" />}
                <div className="flex-1">
                  <p className="font-medium text-sm">{alert.title}</p>
                  <p className="text-xs opacity-80">{alert.message}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Main Content */}
        {/* View Toggle */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewMode === 'table' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <List className="w-4 h-4" />
              Table View
            </button>
            <button
              onClick={() => setViewMode('card')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewMode === 'card' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
              Card View
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewMode === 'calendar' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="schedule-calendar-toggle"
            >
              <Calendar className="w-4 h-4" />
              Calendar
            </button>
          </div>
          <Button size="sm" variant="outline" onClick={() => { setSelectedPhase(phases[0]); setShowAddTaskDialog(true); }}>
            <Plus className="w-4 h-4 mr-1" />
            Add Task
          </Button>
        </div>

        {/* TABLE VIEW */}
        {viewMode === 'table' && (
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2">
              <Card className="overflow-hidden">
                <div className="bg-slate-800 text-white px-4 py-3 flex items-center justify-between">
                  <h3 className="font-semibold">Construction Schedule</h3>
                  <span className="text-sm text-slate-300">{project?.name}</span>
                </div>
                <div className="divide-y divide-slate-200">
                  {phases.length === 0 ? (
                    <div className="p-8 text-center">
                      <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500">No schedule yet. Upload or create tasks.</p>
                    </div>
                  ) : (
                    phases.map(phase => {
                      const phaseTasks = tasks.filter(t => t.phase_id === phase.id);
                      const status = phase.status || 'pending';
                      
                      return (
                        <div key={phase.id}>
                          {/* Phase Header - Like Excel */}
                          <div className={`px-4 py-2 flex items-center justify-between ${
                            status === 'completed' ? 'bg-green-100' :
                            status === 'in_progress' ? 'bg-blue-100' :
                            status === 'delayed' ? 'bg-red-100' :
                            'bg-slate-100'
                          }`}>
                            <div className="flex items-center gap-3">
                              <h4 className="font-bold text-sm uppercase tracking-wide text-slate-800">{phase.name}</h4>
                              {getStatusBadge(status)}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-600">
                                {formatDate(phase.start_date)} - {formatDate(phase.end_date)}
                              </span>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 w-7 p-0"
                                onClick={() => openEditPhase(phase)}
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 w-7 p-0"
                                onClick={() => { setSelectedPhase(phase); setShowAddTaskDialog(true); }}
                              >
                                <Plus className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Tasks - Compact Table */}
                          {phaseTasks.length > 0 ? (
                            <table className="w-full text-sm">
                              <tbody>
                                {phaseTasks.map((task, idx) => (
                                  <tr key={task.id} className="hover:bg-slate-50 border-b border-slate-100 last:border-0">
                                    <td className="px-4 py-2 w-24 text-slate-500">{formatDate(task.start_date)}</td>
                                    <td className="px-4 py-2">
                                      <span className={`${task.status === 'completed' ? 'line-through text-slate-400' : ''}`}>
                                        {task.name}
                                      </span>
                                    </td>
                                    <td className="px-2 py-2 w-24">{getStatusBadge(task.status)}</td>
                                    <td className="px-2 py-2 w-20 text-right">
                                      <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="h-7 w-7 p-0"
                                        onClick={() => openEditTask(task)}
                                      >
                                        <Edit2 className="w-3.5 h-3.5" />
                                      </Button>
                                      <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                        onClick={() => handleDeleteTask(task.id)}
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div className="px-4 py-2 text-xs text-slate-400 italic">No tasks</div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </Card>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-4">
              {/* AI Health Mini Panel */}
              {healthData && (
                <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
                  <CardContent className="pt-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-400" />
                        AI Health
                      </h3>
                    </div>
                    
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold ${
                        healthData.health_score >= 80 ? 'bg-green-500' :
                        healthData.health_score >= 60 ? 'bg-amber-500' :
                        'bg-red-500'
                      }`}>
                        {healthData.grade}
                      </div>
                      <div>
                        <p className="text-sm font-semibold">{healthData.risk_level}</p>
                        <p className="text-xs text-slate-400">{healthData.schedule?.completion_percentage || 0}% complete</p>
                      </div>
                    </div>

                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full border-slate-600 text-white hover:bg-slate-700"
                      onClick={() => setShowHealthDialog(true)}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Approvals */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Approvals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {approvals.length === 0 ? (
                    <p className="text-sm text-slate-500">No pending approvals</p>
                  ) : (
                    <div className="space-y-2">
                      {approvals.slice(0, 5).map(approval => (
                        <div key={approval.id} className={`p-2 rounded text-xs ${
                          approval.status === 'approved' ? 'bg-green-50' :
                          approval.status === 'change_requested' ? 'bg-red-50' :
                          'bg-amber-50'
                        }`}>
                          <p className="font-medium">{approval.contractor?.company || approval.contractor?.name}</p>
                          <p className="text-slate-500">{approval.phase?.name}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* CARD VIEW */}
        {viewMode === 'card' && (
        <div className="grid grid-cols-3 gap-6">
          {/* Phase List */}
          <div className="col-span-2 space-y-4">
            {phases.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-900 mb-2">No schedule yet</h3>
                  <p className="text-sm text-slate-500 mb-4">Upload a schedule or add tasks to phases</p>
                  <Button onClick={() => setShowUploadDialog(true)}>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Schedule
                  </Button>
                </CardContent>
              </Card>
            ) : (
              phases.map(phase => {
                const phaseTasks = tasks.filter(t => t.phase_id === phase.id);
                const isExpanded = expandedPhases[phase.id];
                const contractor = contractors.find(c => c.id === phase.contractor_id);
                const status = phase.status || 'pending';
                
                const statusColors = {
                  completed: 'border-l-green-500 bg-green-50/50',
                  in_progress: 'border-l-blue-500 bg-blue-50/50',
                  delayed: 'border-l-red-500 bg-red-50/50',
                  pending: 'border-l-slate-300'
                };

                return (
                  <Card key={phase.id} className={`overflow-hidden border-l-4 ${statusColors[status] || statusColors.pending}`}>
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                      onClick={() => setExpandedPhases(prev => ({ ...prev, [phase.id]: !prev[phase.id] }))}
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                        <div>
                          <h3 className="font-semibold text-slate-900">{phase.name}</h3>
                          <p className="text-sm text-slate-500">
                            {formatDate(phase.start_date)} - {formatDate(phase.end_date)}
                            {phaseTasks.length > 0 && ` • ${phaseTasks.length} tasks`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(status)}
                        {contractor && getApprovalBadge(phase.id, contractor.id)}
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <>
                        {/* Tasks */}
                        <div className="border-t border-slate-100">
                          {phaseTasks.length > 0 ? (
                            phaseTasks.map((task, idx) => (
                              <div key={task.id} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 border-b border-slate-50 last:border-0">
                                <div className="flex items-center gap-3">
                                  <span className="text-sm text-slate-400 w-6">{idx + 1}</span>
                                  <div>
                                    <p className="text-sm font-medium text-slate-700">{task.name}</p>
                                    {task.description && <p className="text-xs text-slate-500">{task.description}</p>}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-slate-500">
                                    {formatDate(task.start_date)} - {formatDate(task.end_date)}
                                  </span>
                                  {getStatusBadge(task.status)}
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="px-4 py-3 text-sm text-slate-500 italic">No tasks added yet</p>
                          )}
                        </div>
                        
                        {/* Phase Actions */}
                        <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {contractor && (
                              <>
                                <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center text-xs font-medium text-blue-700">
                                  {(contractor.company || contractor.name || '?').charAt(0)}
                                </div>
                                <span className="text-sm text-slate-600">{contractor.company || contractor.name}</span>
                              </>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); setSelectedPhase(phase); setShowAddTaskDialog(true); }}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add Task
                            </Button>
                            {contractor && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    setSelectedPhase(phase); 
                                    setSelectedContractor(contractor.id);
                                    setShowSendScheduleDialog(true); 
                                  }}
                                >
                                  <Send className="w-4 h-4 mr-1" />
                                  Send Schedule
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    setSelectedPhase(phase);
                                    setSelectedContractor(contractor.id);
                                    setShowMessageDialog(true); 
                                  }}
                                >
                                  <MessageSquare className="w-4 h-4 mr-1" />
                                  Message
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </Card>
                );
              })
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-4">
            {/* AI Health Mini Panel */}
            {healthData && (
              <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                      AI Health
                    </h3>
                    <span className="text-xs text-slate-400">Updated now</span>
                  </div>
                  
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold ${
                      healthData.health_score >= 80 ? 'bg-green-500' :
                      healthData.health_score >= 60 ? 'bg-amber-500' :
                      'bg-red-500'
                    }`}>
                      {healthData.grade}
                    </div>
                    <div>
                      <p className={`font-semibold ${
                        healthData.health_score >= 80 ? 'text-green-400' :
                        healthData.health_score >= 60 ? 'text-amber-400' :
                        'text-red-400'
                      }`}>{healthData.risk_level}</p>
                      <p className="text-sm text-slate-400">Score: {healthData.health_score}</p>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Progress</span>
                      <span>{healthData.schedule?.completion_percentage || 0}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Budget</span>
                      <span className={healthData.budget?.variance >= 0 ? 'text-green-400' : 'text-red-400'}>
                        ${Math.abs(healthData.budget?.variance || 0).toLocaleString()} {healthData.budget?.status}
                      </span>
                    </div>
                  </div>

                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-4 border-slate-600 text-white hover:bg-slate-700"
                    onClick={() => setShowHealthDialog(true)}
                  >
                    View Details
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Approvals */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Schedule Approvals
                </CardTitle>
              </CardHeader>
              <CardContent>
                {approvals.length === 0 ? (
                  <p className="text-sm text-slate-500">No pending approvals</p>
                ) : (
                  <div className="space-y-2">
                    {approvals.map(approval => (
                      <div key={approval.id} className={`p-2 rounded-lg ${
                        approval.status === 'approved' ? 'bg-green-50' :
                        approval.status === 'change_requested' ? 'bg-red-50' :
                        'bg-amber-50'
                      }`}>
                        <p className="text-sm font-medium">{approval.contractor?.company || approval.contractor?.name}</p>
                        <p className="text-xs text-slate-500">{approval.phase?.name}</p>
                        {approval.status === 'change_requested' && approval.change_request_reason && (
                          <p className="text-xs text-red-600 mt-1">{approval.change_request_reason}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        )}

        {/* CALENDAR VIEW (read-only month grid) */}
        {viewMode === 'calendar' && (
          <ScheduleCalendar tasks={tasks} phases={phases} />
        )}
      </div>

      {/* Upload Schedule Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Schedule</DialogTitle>
            <DialogDescription>Import your construction schedule from Excel, CSV, PDF, or photo</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div 
              className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-blue-500 transition-colors cursor-pointer"
              onClick={() => document.getElementById('schedule-upload')?.click()}
            >
              <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="font-medium text-slate-900">
                {uploadFile ? uploadFile.name : 'Click to upload or drag & drop'}
              </p>
              <p className="text-sm text-slate-500">Excel, CSV, PDF, or Images (PNG, JPG)</p>
              <input 
                id="schedule-upload"
                type="file" 
                accept=".xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg,.webp,.heic,.heif" 
                className="hidden"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
            <Button onClick={handleUploadSchedule} disabled={!uploadFile || isUploading}>
              {isUploading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {isUploading ? 'Analyzing...' : 'Import Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Task Dialog */}
      
      {/* Schedule Comparison Dialog */}
      <Dialog open={showComparisonDialog} onOpenChange={setShowComparisonDialog}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-blue-600" />
              Review & Map Schedule Items
            </DialogTitle>
            <DialogDescription>
              Your uploaded schedule items are shown below. <strong>Optionally link them to existing budget phases</strong> or leave as standalone schedule items.
            </DialogDescription>
          </DialogHeader>
          
          {comparisonData && (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Info Banner */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Option B Mode:</strong> Schedule items will <strong>not</strong> create new phases. 
                  Use the dropdowns to link items to existing budget phases, or leave unlinked for timeline-only display.
                </p>
              </div>
              
              {/* Summary */}
              <div className="grid grid-cols-4 gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">{comparisonData.summary?.total_tasks || comparisonData.comparison?.reduce((sum, p) => sum + (p.tasks?.length || 0), 0) || 0}</p>
                  <p className="text-xs text-slate-500">Total Items</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{comparisonData.summary?.new_tasks || 0}</p>
                  <p className="text-xs text-slate-500">New Items</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">{Object.values(phaseMappings).filter(v => v).length}</p>
                  <p className="text-xs text-slate-500">Linked to Phases</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-500">{(comparisonData.summary?.total_tasks || comparisonData.comparison?.reduce((sum, p) => sum + (p.tasks?.length || 0), 0) || 0) - Object.values(phaseMappings).filter(v => v).length}</p>
                  <p className="text-xs text-slate-500">Standalone</p>
                </div>
              </div>
              
              {/* Select All */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => selectAllChanges(true)}>
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Select All
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => selectAllChanges(false)}>
                    <X className="w-4 h-4 mr-1" />
                    Deselect All
                  </Button>
                </div>
                <span className="text-sm text-slate-500">File: {comparisonData.filename}</span>
              </div>
              
              {/* Comparison List with Phase Mapping */}
              <div className="flex-1 overflow-y-auto border rounded-lg">
                {comparisonData.comparison?.map((phase, pIdx) => (
                  <div key={pIdx} className="border-b last:border-0">
                    {/* Phase/Group Row */}
                    <div className={`flex items-center gap-3 p-3 ${
                      phase.is_new ? 'bg-green-50' : phase.has_changes ? 'bg-blue-50' : 'bg-slate-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={selectedChanges[`phase_${pIdx}`] !== false && phase.has_changes}
                        onChange={() => togglePhaseSelection(pIdx)}
                        disabled={!phase.has_changes}
                        className="w-4 h-4"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-900">{phase.name}</span>
                          {phase.is_new && <Badge className="bg-green-100 text-green-700 text-xs">NEW GROUP</Badge>}
                        </div>
                        {phase.has_changes && (
                          <div className="flex items-center gap-4 mt-1 text-xs">
                            <span className="text-blue-600 font-medium">
                              {formatDate(phase.new?.start_date) || 'N/A'} → {formatDate(phase.new?.end_date) || 'N/A'}
                            </span>
                          </div>
                        )}
                      </div>
                      {/* Phase Mapping Dropdown */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">Link to:</span>
                        <select
                          className="text-sm border border-slate-300 rounded px-2 py-1 bg-white min-w-[150px]"
                          value={phaseMappings[`phase_${pIdx}`] || ''}
                          onChange={(e) => setPhaseMappings(prev => ({ ...prev, [`phase_${pIdx}`]: e.target.value || null }))}
                        >
                          <option value="">-- Standalone (No Phase) --</option>
                          {phases.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Tasks */}
                    {phase.tasks?.length > 0 && (
                      <div className="pl-8">
                        {phase.tasks.map((task, tIdx) => (
                          <div key={tIdx} className={`flex items-center gap-3 p-2 border-t border-slate-100 ${
                            task.is_new ? 'bg-green-50/50' : task.has_changes ? 'bg-blue-50/50' : ''
                          }`}>
                            <input
                              type="checkbox"
                              checked={selectedChanges[`task_${pIdx}_${tIdx}`] !== false && task.has_changes}
                              onChange={() => toggleTaskSelection(pIdx, tIdx)}
                              disabled={!task.has_changes}
                              className="w-4 h-4"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-slate-700">{task.name}</span>
                                {task.is_new && <Badge className="bg-green-100 text-green-700 text-[10px]">NEW</Badge>}
                                {!task.is_new && task.has_changes && <Badge className="bg-blue-100 text-blue-700 text-[10px]">UPDATED</Badge>}
                              </div>
                              {task.has_changes && (
                                <div className="flex items-center gap-4 mt-0.5 text-xs">
                                  {task.current && (
                                    <span className="text-slate-400">
                                      {formatDate(task.current.start_date) || 'N/A'}
                                    </span>
                                  )}
                                  <span className="text-blue-600">
                                    → {formatDate(task.new?.start_date) || 'N/A'}
                                  </span>
                                </div>
                              )}
                            </div>
                            {/* Task-level mapping (inherits from group if not set) */}
                            <div className="flex items-center gap-1">
                              {(phaseMappings[`task_${pIdx}_${tIdx}`] || phaseMappings[`phase_${pIdx}`]) ? (
                                <Badge className="bg-purple-100 text-purple-700 text-[10px]">
                                  Linked
                                </Badge>
                              ) : (
                                <Badge className="bg-slate-100 text-slate-500 text-[10px]">
                                  Standalone
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setShowComparisonDialog(false); setComparisonData(null); setPhaseMappings({}); }}>
              Cancel
            </Button>
            <Button onClick={handleApplyChanges} className="bg-blue-600 hover:bg-blue-700">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Import Schedule Items
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddTaskDialog} onOpenChange={setShowAddTaskDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Task to {selectedPhase?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Task Name</label>
              <Input 
                value={newTask.name}
                onChange={(e) => setNewTask(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Plumbing Walkthrough"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Description</label>
              <Textarea 
                value={newTask.description}
                onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Start Date</label>
                <Input 
                  type="date"
                  value={newTask.start_date}
                  onChange={(e) => setNewTask(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">End Date</label>
                <Input 
                  type="date"
                  value={newTask.end_date}
                  onChange={(e) => setNewTask(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleAddTask}>
              <Plus className="w-4 h-4 mr-2" />
              Add Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Schedule Dialog */}
      <Dialog open={showSendScheduleDialog} onOpenChange={setShowSendScheduleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Schedule to Contractor</DialogTitle>
            <DialogDescription>The contractor will receive an email to review and approve the schedule</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              <strong>Phase:</strong> {selectedPhase?.name}<br />
              <strong>Dates:</strong> {formatDate(selectedPhase?.start_date)} - {formatDate(selectedPhase?.end_date)}
            </p>
            <div>
              <label className="text-sm font-medium text-slate-700">Select Contractor</label>
              <Select value={selectedContractor} onValueChange={setSelectedContractor}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose contractor" />
                </SelectTrigger>
                <SelectContent>
                  {contractors.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendScheduleDialog(false)}>Cancel</Button>
            <Button onClick={handleSendSchedule}>
              <Send className="w-4 h-4 mr-2" />
              Send Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Message</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea 
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Type your message..."
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageDialog(false)}>Cancel</Button>
            <Button onClick={handleSendMessage}>
              <Send className="w-4 h-4 mr-2" />
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Health Dialog */}
      <Dialog open={showHealthDialog} onOpenChange={setShowHealthDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              AI Health Analysis
            </DialogTitle>
          </DialogHeader>
          {healthData && (
            <div className="py-4 space-y-6">
              {/* Health Score */}
              <div className="flex items-center gap-6">
                <div className={`w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold text-white ${
                  healthData.health_score >= 80 ? 'bg-green-500' :
                  healthData.health_score >= 60 ? 'bg-amber-500' :
                  'bg-red-500'
                }`}>
                  {healthData.grade}
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{healthData.risk_level}</p>
                  <p className="text-slate-500">Health Score: {healthData.health_score}/100</p>
                </div>
              </div>

              {/* Schedule Stats */}
              <div>
                <h4 className="font-medium text-slate-900 mb-2">Schedule Progress</h4>
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-slate-900">{healthData.schedule?.completed || 0}</p>
                    <p className="text-xs text-slate-500">Completed</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-blue-600">{healthData.schedule?.in_progress || 0}</p>
                    <p className="text-xs text-slate-500">In Progress</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-red-600">{healthData.schedule?.behind || 0}</p>
                    <p className="text-xs text-slate-500">Behind</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-slate-600">{healthData.schedule?.not_started || 0}</p>
                    <p className="text-xs text-slate-500">Not Started</p>
                  </div>
                </div>
              </div>

              {/* Budget */}
              <div>
                <h4 className="font-medium text-slate-900 mb-2">Budget Status</h4>
                <div className={`p-4 rounded-lg ${healthData.budget?.variance >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className={`text-2xl font-bold ${healthData.budget?.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${Math.abs(healthData.budget?.variance || 0).toLocaleString()} {healthData.budget?.status}
                  </p>
                  <p className="text-sm text-slate-500">
                    Estimated: ${(healthData.budget?.total_estimated || 0).toLocaleString()} • 
                    Actual: ${(healthData.budget?.total_actual || 0).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* AI Insights */}
              {healthData.insights?.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">AI Insights</h4>
                  <div className="space-y-2">
                    {healthData.insights.map((insight, idx) => (
                      <div key={idx} className={`flex items-start gap-2 p-3 rounded-lg ${
                        insight.type === 'warning' ? 'bg-amber-50' :
                        insight.type === 'success' ? 'bg-green-50' :
                        'bg-blue-50'
                      }`}>
                        {insight.type === 'warning' ? <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" /> :
                         insight.type === 'success' ? <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" /> :
                         <TrendingUp className="w-5 h-5 text-blue-500 mt-0.5" />}
                        <p className="text-sm">{insight.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cash Flow Dialog */}
      <Dialog open={showCashFlowDialog} onOpenChange={setShowCashFlowDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              Cash Flow Forecast
            </DialogTitle>
          </DialogHeader>
          {cashFlowData && (
            <div className="py-4 space-y-6">
              {/* Status */}
              <div className={`p-4 rounded-lg ${
                cashFlowData.status === 'healthy' ? 'bg-green-50' :
                cashFlowData.status === 'caution' ? 'bg-amber-50' :
                'bg-red-50'
              }`}>
                <p className={`font-bold text-lg ${
                  cashFlowData.status === 'healthy' ? 'text-green-700' :
                  cashFlowData.status === 'caution' ? 'text-amber-700' :
                  'text-red-700'
                }`}>{cashFlowData.status_message}</p>
                <p className="text-sm mt-1">
                  Funds Available: <strong>${(cashFlowData.funds_available || 0).toLocaleString()}</strong> • 
                  Next 2 Weeks: <strong>${(cashFlowData.next_2_weeks || 0).toLocaleString()}</strong>
                </p>
              </div>

              {/* Weekly Forecast */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3">8-Week Forecast</h4>
                <div className="space-y-2">
                  {cashFlowData.weekly_forecast?.map(week => (
                    <div key={week.week} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-900">Week {week.week}</p>
                        <p className="text-xs text-slate-500">{week.week_start} - {week.week_end}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${week.total > 0 ? 'text-slate-900' : 'text-slate-400'}`}>
                          ${week.total.toLocaleString()}
                        </p>
                        {week.payments?.length > 0 && (
                          <p className="text-xs text-slate-500">{week.payments.length} payment(s)</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Optimal Draw Timing */}
              {cashFlowData.optimal_draw_timing && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-1">Recommended Draw Request</h4>
                  <p className="text-sm text-blue-700">{cashFlowData.optimal_draw_timing.reason}</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Submit by <strong>{cashFlowData.optimal_draw_timing.date_range}</strong> for ${cashFlowData.optimal_draw_timing.recommended_amount?.toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={showEditTaskDialog} onOpenChange={setShowEditTaskDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
          </DialogHeader>
          {editTask && (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Task Name</label>
                <Input 
                  value={editTask.name}
                  onChange={(e) => setEditTask(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Description</label>
                <Textarea 
                  value={editTask.description || ''}
                  onChange={(e) => setEditTask(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Start Date</label>
                  <Input 
                    type="date"
                    value={editTask.start_date}
                    onChange={(e) => setEditTask(prev => ({ ...prev, start_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">End Date</label>
                  <Input 
                    type="date"
                    value={editTask.end_date}
                    onChange={(e) => setEditTask(prev => ({ ...prev, end_date: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Status</label>
                <Select value={editTask.status || 'not_started'} onValueChange={(v) => setEditTask(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="behind">Behind</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleEditTask}>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Phase Dialog */}
      <Dialog open={showEditPhaseDialog} onOpenChange={setShowEditPhaseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Phase</DialogTitle>
          </DialogHeader>
          {editPhase && (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Phase Name</label>
                <Input 
                  value={editPhase.name}
                  onChange={(e) => setEditPhase(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Start Date</label>
                  <Input 
                    type="date"
                    value={editPhase.start_date}
                    onChange={(e) => setEditPhase(prev => ({ ...prev, start_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">End Date</label>
                  <Input 
                    type="date"
                    value={editPhase.end_date}
                    onChange={(e) => setEditPhase(prev => ({ ...prev, end_date: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Status</label>
                <Select value={editPhase.status || 'pending'} onValueChange={(v) => setEditPhase(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="delayed">Delayed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditPhaseDialog(false)}>Cancel</Button>
            <Button onClick={handleEditPhase}>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Schedule Items Dialog - Option 3 */}
      <Dialog open={showSendItemsDialog} onOpenChange={setShowSendItemsDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-green-600" />
              Send Schedule to Contractor
            </DialogTitle>
            <DialogDescription>
              Select the schedule items you want to send and choose a contractor
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col py-4">
            {/* Contractor Selection */}
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 mb-2 block">Select Contractor</label>
              {contractors.length === 0 ? (
                <p className="text-sm text-slate-500 italic">No contractors available. Add contractors in the Project Hub first.</p>
              ) : (
                <Select value={selectedContractor || undefined} onValueChange={(val) => setSelectedContractor(val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a contractor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contractors.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.company || c.name} {c.email ? `(${c.email})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Item Selection */}
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-slate-700">Select Items to Send</label>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={selectAllTasksToSend}>
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  All
                </Button>
                <Button size="sm" variant="outline" onClick={deselectAllTasksToSend}>
                  <X className="w-3 h-3 mr-1" />
                  None
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto border rounded-lg mb-4">
              {tasks.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  No schedule items yet. Upload a schedule first.
                </div>
              ) : (
                tasks.map(task => (
                  <div
                    key={task.id}
                    onClick={() => toggleTaskToSend(task.id)}
                    className={`flex items-center gap-3 p-3 border-b last:border-0 cursor-pointer transition-all ${
                      selectedTasksToSend[task.id] ? 'bg-green-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTasksToSend[task.id] || false}
                      onChange={() => toggleTaskToSend(task.id)}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{task.name}</span>
                        {task.linked_phase_id || task.phase_id ? (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">
                            {phases.find(p => p.id === (task.linked_phase_id || task.phase_id))?.name || 'Linked'}
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-500 text-xs">Standalone</Badge>
                        )}
                      </div>
                      <div className="text-sm text-slate-500">
                        {formatDate(task.start_date)} {task.end_date && task.end_date !== task.start_date && `→ ${formatDate(task.end_date)}`}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Optional Message */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Message (Optional)</label>
              <textarea
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
                placeholder="Add a note for the contractor..."
                className="w-full p-3 border rounded-lg resize-none h-20 text-sm"
              />
            </div>

            {/* Summary */}
            <div className="mt-4 p-3 bg-slate-50 rounded-lg flex items-center justify-between">
              <span className="text-sm text-slate-600">
                <strong>{Object.values(selectedTasksToSend).filter(Boolean).length}</strong> items selected
              </span>
              {selectedContractor && (
                <span className="text-sm text-slate-600">
                  Sending to: <strong>{contractors.find(c => c.id === selectedContractor)?.company || contractors.find(c => c.id === selectedContractor)?.name}</strong>
                </span>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSendItemsDialog(false); setSelectedTasksToSend({}); setSendMessage(''); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendScheduleItems} 
              className="bg-green-600 hover:bg-green-700"
              disabled={Object.values(selectedTasksToSend).filter(Boolean).length === 0 || !selectedContractor}
            >
              <Send className="w-4 h-4 mr-2" />
              Send {Object.values(selectedTasksToSend).filter(Boolean).length} Items
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================
// ScheduleCalendar — read-only month grid of schedule tasks
// ============================================================
function ScheduleCalendar({ tasks = [], phases = [] }) {
  const parse = (d) => { if (!d) return null; const x = new Date(d); return isNaN(x.getTime()) ? null : x; };
  const allDates = tasks.flatMap(t => [parse(t.start_date), parse(t.end_date)]).filter(Boolean);
  const initial = allDates.length ? allDates.sort((a, b) => a - b)[0] : new Date();
  const [cursor, setCursor] = React.useState(new Date(initial.getFullYear(), initial.getMonth(), 1));

  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const firstDay = new Date(y, m, 1);
  const startWeekday = firstDay.getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const monthLabel = cursor.toLocaleString('default', { month: 'long', year: 'numeric' });

  const phaseColors = ['#2563eb', '#059669', '#d97706', '#7c3aed', '#dc2626', '#0891b2', '#db2777', '#65a30d'];
  const colorFor = (t) => {
    const idx = phases.findIndex(p => p.id === t.phase_id);
    return phaseColors[(idx >= 0 ? idx : 0) % phaseColors.length];
  };

  const tasksOnDay = (day) => {
    const cell = new Date(y, m, day);
    cell.setHours(12, 0, 0, 0);
    return tasks.filter(t => {
      const s = parse(t.start_date), e = parse(t.end_date) || parse(t.start_date);
      if (!s) return false;
      const s0 = new Date(s); s0.setHours(0, 0, 0, 0);
      const e0 = new Date(e); e0.setHours(23, 59, 59, 0);
      return cell >= s0 && cell <= e0;
    });
  };

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const today = new Date();
  const isToday = (day) => today.getFullYear() === y && today.getMonth() === m && today.getDate() === day;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="schedule-calendar-view">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-900">{monthLabel}</h3>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setCursor(new Date(y, m - 1, 1))} data-testid="cal-prev">
            <ChevronRight className="w-4 h-4 rotate-180" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))}>
            Today
          </Button>
          <Button size="sm" variant="outline" onClick={() => setCursor(new Date(y, m + 1, 1))} data-testid="cal-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="bg-slate-50 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500">{d}</div>
        ))}
        {cells.map((day, i) => (
          <div key={i} className={`bg-white min-h-[92px] p-1.5 ${day && isToday(day) ? 'ring-2 ring-inset ring-blue-400' : ''}`}>
            {day && (
              <>
                <div className={`text-xs font-semibold mb-1 ${isToday(day) ? 'text-blue-600' : 'text-slate-500'}`}>{day}</div>
                <div className="space-y-1">
                  {tasksOnDay(day).slice(0, 3).map((t, j) => (
                    <div key={j} className="text-[10px] leading-tight px-1.5 py-0.5 rounded text-white truncate"
                      style={{ backgroundColor: colorFor(t) }} title={t.name} data-testid="cal-task">
                      {t.name}
                    </div>
                  ))}
                  {tasksOnDay(day).length > 3 && (
                    <div className="text-[10px] text-slate-400 pl-1">+{tasksOnDay(day).length - 3} more</div>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      {tasks.length === 0 && (
        <p className="text-sm text-slate-500 text-center mt-4">No scheduled tasks yet. Add tasks or import a schedule to see them here.</p>
      )}
    </div>
  );
}

export default ProjectSchedule;
